// src/pages/Changelog.jsx
import React from 'react';
import { Box, Heading, Text, VStack, Flex } from '@chakra-ui/react';

const changelogEntries = [
    {
      changeNumber: 70,
      date: 'Sep 17, 2026',
      type: 'Added',
      title: 'Free cash flow calculation',
      details: [
        "Shows derived free cash flow (operating cash flow minus capital expenditures) for comparable quarters"
      ]
    },
    {
      changeNumber: 69,
      date: 'Sep 16, 2026',
      type: 'Added',
      title: 'Recent earnings reports',
      details: [
        "Shows as-filed statements, earnings releases, and available presentations inside Financial Statements",
        "Compares supported metrics across fiscal quarters with shareable chart images",
        "Includes combined income statements and preserves the newest matching earnings documents"
      ]
    },
    {
      changeNumber: 68,
      date: 'Sep 13, 2026',
      type: 'Improved',
      title: 'Extended login session persistence',
      details: [
        "Remembers returning browsers for 90 days so saved login sessions can be restored",
        "Fixes a one-day cutoff that could skip session restoration; actual login duration still depends on Auth0 token settings"
      ]
    },
    {
      changeNumber: 67,
      date: 'Sep 2, 2026',
      type: 'Added',
      title: 'Company classification and market cap data',
      details: [
        "Displays sector and industry information from Nasdaq or SEC SIC fallback",
        "Shows market capitalization for companies"
      ]
    },
    {
      changeNumber: 66,
      date: 'Sep 1, 2026',
      type: 'Added',
      title: 'Pre-IPO company search',
      details: [
        "Shows proposed ticker and exchange information",
        "Allows navigation to SEC filings by CIK"
      ]
    },
    {
      changeNumber: 65,
      date: 'Sep 1, 2026',
      type: 'Added',
      title: 'Pre-IPO company search',
      details: [
        "Shows proposed ticker and exchange information",
        "Allows navigation to SEC filings by CIK"
      ]
    },
    {
      changeNumber: 64,
      date: 'Aug 28, 2026',
      type: 'Added',
      title: 'LifeMD metrics support',
      details: [
        "New metric: Active Patient Subscribers",
        "New metric: Customers and Patients Helped"
      ]
    },
    {
      changeNumber: 63,
      date: 'Aug 22, 2026',
      type: 'Added',
      title: 'Public API & MCP integration',
      details: [
        "Enables programmatic access to stock data through API keys",
        "Supports integration with AI tools like Claude Desktop and Cursor"
      ]
    },
    {
      changeNumber: 62,
      date: 'Aug 10, 2026',
      type: 'Added',
      title: 'Improved stock page SEO and loading',
      details: [
        "Adds pre-rendered content and internal links before React loads",
        "Provides better crawlable content for search engines"
      ]
    },
    {
      changeNumber: 61,
      date: 'Aug 9, 2026',
      type: 'Added',
      title: 'API & MCP documentation page',
      details: [
        "Adds /docs with authentication, endpoints, examples, and MCP setup",
        "Linked from navbar, footer, and Settings"
      ]
    },
    {
      changeNumber: 60,
      date: 'Aug 8, 2026',
      type: 'Added',
      title: 'Public API and MCP access',
      details: [
        "Adds /api/v1 endpoints with API key auth and free-tier rate limits",
        "Adds Settings API key management and stdio MCP server"
      ]
    },
    {
      changeNumber: 59,
      date: 'Aug 8, 2026',
      type: 'Added',
      title: 'SEC filings and UI enhancements',
      details: [
        "Adds Form 4 transaction summaries to SEC filings table",
        "Shows pending status while loading Form 4 data",
        "Includes pagination controls for large filing histories",
        "Adds contextual notes to calculation tooltips",
        "Improves navigation styling with updated colors and hover states",
        "Enhances Reverse DCF input styling and mobile responsiveness"
      ]
    },
    {
      changeNumber: 58,
      date: 'Aug 8, 2026',
      type: 'Added',
      title: 'UI improvements and calculation notes',
      details: [
        "Adds contextual notes to calculation tooltips",
        "Updates navbar and sidebar colors with improved visual hierarchy",
        "Enhances Reverse DCF input styling with visual highlighting"
      ]
    },
    {
      changeNumber: 57,
      date: 'Aug 5, 2026',
      type: 'Improved',
      title: 'Authentication flow improvements',
      details: [
        "Adds loading screen during auth redirects",
        "Improves handling of post-login redirects"
      ]
    },
    {
      changeNumber: 56,
      date: 'Jul 28, 2026',
      type: 'Added',
      title: 'Drag-to-compare feature on Stock Price chart',
      details: [
        "Click-drag across two points to see price change with floating badge",
        "Adds timeline controls (1M/6M/YTD/1Y/5Y/Max) for price history"
      ]
    },
    {
      changeNumber: 55,
      date: 'Jul 17, 2026',
      type: 'Added',
      title: 'Since-date review mode',
      details: [
        "Adds floating control for selecting review dates that persists across tabs and sessions",
        "Filters SEC filings and highlights chart data based on selected review date"
      ]
    },
    {
      changeNumber: 54,
      date: 'Jul 15, 2026',
      type: 'Improved',
      title: 'Better handling of unavailable stock prices',
      details: [
        "Shows warning message when current stock price is unavailable",
        "Falls back to SEC-reported shares outstanding data when price is unavailable"
      ]
    },
    {
      changeNumber: 53,
      date: 'Jul 15, 2026',
      type: 'Improved',
      title: 'Better handling of unavailable stock prices',
      details: [
        "Shows warning message when current stock price is unavailable",
        "Falls back to SEC-reported shares outstanding data when price is unavailable"
      ]
    },
    {
      changeNumber: 52,
      date: 'Jul 14, 2026',
      type: 'Added',
      title: 'Full DCF Analysis Workbench',
      details: [
        "Replaces Analysis tab with guided valuation workspace",
        "Adds revenue-driven DCF calculations with terminal value and sensitivity analysis",
        "Enables DCF scenario saving, loading, and comparison"
      ]
    },
    {
      changeNumber: 51,
      date: 'Jul 3, 2026',
      type: 'Improved',
      title: 'Chat input submits on Enter key',
      details: [
        "Messages can now be sent by pressing Enter in addition to clicking Send button"
      ]
    },
    {
      changeNumber: 50,
      date: 'Jun 28, 2026',
      type: 'Added',
      title: 'Stock price history and Snapshot features',
      details: [
        "Added cache-backed stock price history charts to Charts tab",
        "Added Snapshot history tab for point-in-time stock review"
      ]
    },
    {
      changeNumber: 49,
      date: 'Jun 27, 2026',
      type: 'Added',
      title: 'Stock price history charts',
      details: [
        "Adds cache-backed stock price history to the Charts tab with a Stock Price dataset option",
        "Adds hindsight-safe Snapshot price charts that only show prices through the selected snapshot date",
        "Uses adjusted close for long-range charting while keeping raw close available in chart details"
      ]
    },
    {
      changeNumber: 48,
      date: 'Jun 25, 2026',
      type: 'Changed',
      title: 'Tiingo-first stock prices',
      details: [
        "Uses Tiingo as the primary end-of-day price provider for current stock prices and Snapshot history",
        "Keeps cached prices first and makes Marketstack fallback explicitly opt-in"
      ]
    },
    {
      changeNumber: 47,
      date: 'Jun 24, 2026',
      type: 'Added',
      title: 'Snapshot history tab',
      details: [
        "Choose a past date from a stock page to review filings, financial facts, and price status available at that time",
        "Shows filed dates, accession/source metadata, and historical price availability without leaking future filings",
        "Adds redaction mode and a today-vs-snapshot outcome panel for backtesting practice"
      ]
    },
    {
      changeNumber: 46,
      date: 'Jun 23, 2026',
      type: 'Fixed',
      title: 'Improved SEO routing and page availability',
      details: [
        "Fixes sitemap.xml availability for search engines",
        "Ensures stock pages load correctly instead of showing 404 errors",
        "Makes public metric pages indexable with canonical URLs"
      ]
    },
    {
      changeNumber: 45,
      date: 'Jun 16, 2026',
      type: 'Added',
      title: 'Reverse DCF improvements',
      details: [
        "Adds ability to save assumptions",
        "Adds expandable market cap calculation section with detailed inputs"
      ]
    },
    {
      changeNumber: 44,
      date: 'Jun 16, 2026',
      type: 'Added',
      title: 'Reverse DCF improvements',
      details: [
        "Adds ability to save assumptions",
        "Adds expandable market cap calculation section with detailed inputs"
      ]
    },
    {
      changeNumber: 43,
      date: 'Jun 14, 2026',
      type: 'Added',
      title: 'Table copy and filing data improvements',
      details: [
        "New 'Copy' button to export metric tables as TSV",
        "Enhanced filing data display with external link icons",
        "Added separate columns for Form and Filing Date"
      ]
    },
    {
      changeNumber: 42,
      date: 'Jun 14, 2026',
      type: 'Added',
      title: 'Improved custom metric preview',
      details: [
        "Adds loading status for full history scans",
        "Increases maximum filings for previews from 12 to 40"
      ]
    },
    {
      changeNumber: 40,
      date: 'Jun 14, 2026',
      type: 'Changed',
      title: 'Adjusts custom metric preview limits',
      details: [
        "Reduces maximum filings shown in custom metric preview from 40 to 12"
      ]
    },
    {
      changeNumber: 38,
      date: 'Jun 14, 2026',
      type: 'Added',
      title: 'Custom metrics tracking',
      details: [
        "New 'Custom Metrics' tab in Charts page",
        "View historical trends for company-specific metrics",
        "Links to source SEC filings for each data point"
      ]
    },
    {
      changeNumber: 29,
      date: 'Jun 11, 2026',
      type: 'Added',
      title: 'Stock home valuation dashboard',
      details: [
        "Replaces the stock Home tab with valuation shortcuts, saved Reverse DCF scenarios, recent SEC filings, insider activity, and analysis links",
        "Improves Reverse DCF market cap accuracy with stronger shares outstanding lookups and source tooltips",
        "Shows dual-class share breakdowns when available"
      ]
    },
    {
      changeNumber: 28,
      date: 'Jun 7, 2026',
      type: 'Fixed',
      title: 'Stock basket login recovery',
      details: [
        "Redirects users back through login when basket pages hit recoverable Auth0 token errors",
        "Avoids generic failure messages for expired sessions on basket list, detail, and create pages"
      ]
    },
    {
      changeNumber: 27,
      date: 'Jun 2, 2026',
      type: 'Improved',
      title: 'Stock chart loading and accessibility',
      details: [
        "Loads larger charting libraries only when stock charts are opened",
        "Adds accessibility labels to chat, submission, and cash-flow inputs",
        "Improves compatibility for browsers that do not support newer array sorting APIs"
      ]
    },
    {
      changeNumber: 26,
      date: 'Jun 1, 2026',
      type: 'Added',
      title: 'Editable Reverse DCF market cap components',
      details: [
        "Breaks market cap into share price, shares outstanding, and calculated market cap fields",
        "Lets users edit any component while the related values stay in sync",
        "Clears dependent values when an input is emptied to avoid stale calculations"
      ]
    },
    {
      changeNumber: 25,
      date: 'Jun 1, 2026',
      type: 'Fixed',
      title: 'Reverse DCF stock price refresh',
      details: [
        "Prevents old cached stock prices from keeping market cap values stale during a session",
        "Shows cached price data immediately while refreshing in the background"
      ]
    },
    {
      changeNumber: 24,
      date: 'May 31, 2026',
      type: 'Added',
      title: 'Reverse DCF saving',
      details: [
        "Users can now save and manage their reverse DCF calculations",
        "Supports up to 50 saved analyses with custom names"
      ]
    },
    {
      changeNumber: 22,
      date: 'May 30, 2026',
      type: 'Added',
      title: 'Persists SEC filings filter selections',
      details: [
        "Saves custom filter forms and selected filters in browser storage",
        "Restores previous filter state when returning to the page"
      ]
    },
    {
      changeNumber: 20,
      date: 'May 25, 2026',
      type: 'Improved',
      title: 'Tooltip display improvements',
      details: [
        "Fixes tooltips appearing behind other elements by adding proper z-index management"
      ]
    },
    {
      changeNumber: 19,
      date: 'May 19, 2026',
      type: 'Added',
      title: '8-K search always visible',
      details: [
        "Search box now appears regardless of active filter",
        "Search temporarily overrides current filter to show matching 8-K filings"
      ]
    },
    {
      changeNumber: 17,
      date: 'May 4, 2026',
      type: 'Added',
      title: 'Improved chart export functionality',
      details: [
        "Adds consistent white background for exported charts",
        "Enhances watermark visibility in exported charts"
      ]
    },
    {
      changeNumber: 16,
      date: 'May 1, 2026',
      type: 'Added',
      title: 'Custom SEC filing filters',
      details: [
        "Save and reuse custom SEC filing filter configurations",
        "Filter preferences persist across sessions",
        "Edit and delete saved filters",
        "Requires login to save filters"
      ]
    },
    {
      changeNumber: 15,
      date: 'Apr 7, 2026',
      type: 'Changed',
      title: 'SEC Filings master tag filter improved',
      details: [
        "Empty selection now shows all filings instead of none",
        "More intuitive: filter out tags you don't want",
        "'Clear all' resets to show everything"
      ]
    },
    {
      changeNumber: 14,
      date: 'Apr 2, 2026',
      type: 'Added',
      title: 'Chart line style toggle',
      details: [
        "Adds switch control for smooth/straight lines in stock charts",
        "Remembers user preference between sessions"
      ]
    },
    {
      changeNumber: 13,
      date: 'Mar 30, 2026',
      type: 'Improved',
      title: 'Session management',
      details: [
        "Users now stay signed in longer with refresh tokens",
        "More secure session cookie handling"
      ]
    },
    {
      changeNumber: 12,
      date: 'Mar 27, 2026',
      type: 'Added',
      title: 'Expanded terminal growth options',
      details: [
        "Allows negative terminal growth rates (down to -100%) for modeling shrinking businesses",
        "Adds warning messages for extreme or unusual terminal growth values"
      ]
    },
    {
      changeNumber: 11,
      date: 'Mar 13, 2026',
      type: 'Added',
      title: 'Historical financial data charts',
      details: [
        "Shows revenue, net income, operating cash flow, and balance sheet items",
        "Includes data back to 2007 for many companies"
      ]
    },
    {
      changeNumber: 10,
      date: 'Dec 31, 2025',
      type: 'Changed',
      title: 'SEC Filings tab: improved current reports browsing',
      details: [
        'Filter current reports by master tags (multi-select) and search within filings',
        'Cleaner table rows: master tags are emphasized and the form type is now the main “open document” link',
        'Added a “Showing X out of Y entries” count to make filtering clearer',
      ],
    },
    {
      changeNumber: 9,
      date: 'Dec 25, 2025',
      type: 'Added',
      title: 'AI-tagged SEC 8-Ks in the SEC filings tab',
      details: [
        '8-K / 8-K/A filings now show an AI-generated 140-character one-liner summary',
        'Added consistent, color-coded master tags plus freeform tags for extra context',
        'Summaries/tags update automatically without requiring a page refresh'
      ]
    },
    {
      changeNumber: 8,
      date: 'Dec 19, 2025',
      type: 'Changed',
      title: 'Added hamburger menu for mobile navigation',
      details: [
        'Navigation links now accessible on mobile devices through slide-out drawer menu'
      ]
    },
    {
      changeNumber: 7,
      date: 'Dec 19, 2025',
      type: 'Changed',
      title: 'Enhanced Reverse DCF page with stock price integration and auto-calculation',
      details: [
        'Implemented shares outstanding lookup to auto-calculate market cap',
        'Added net debt calculation from long-term debt minus cash & cash equivalents',
        'Created reusable CalculationTooltip component for financial calculations',
        'Added informative tooltips for Market Cap and Net Debt showing breakdowns',
        'Implemented number formatting with commas for better readability',
        'Support for negative numbers in Net Debt and Free Cash Flow inputs',
        'Removed calculate button - calculations now run automatically',
        'Auto-fills Market Cap and Net Debt from fetched data when available'
      ]
    },
    {
      changeNumber: 6,
      date: 'Dec 19, 2025',
      type: 'Fixed',
      title: 'Fixed bug causing the recent stocks feature to break',

    },
    {
      changeNumber: 5,
      date: 'Dec 18, 2025',
      type: 'Added',
      title: 'Reverse DCF tab (beta)',
      details: [
        'New "Reverse DCF" page under stock analysis that solves for the implied FCF growth rate given a market cap',
        'Inputs: Market Cap, Net Debt, TTM FCF, forecast years, WACC, and terminal growth rate',
        'This feature is in rough beta and may have edge cases or UI polish needed'
      ]
    },
    {
      changeNumber: 4,
      date: 'Dec 17, 2025',
      type: 'Changed',
      title: 'Sidebar tooltips and financial tabs fix',
      details: [
        'Collapsed sidebar items now show tooltips with their labels',
        'Financial statements tabs now use the correct Chakra API and safer API URL fallback to prevent runtime errors'
      ]
    },
    {
      changeNumber: 3,
      date: 'Dec 16, 2025',
      type: 'Changed',
      title: 'Performance improvements and stability fixes',
      details: [
        'Made backend changes to speed up load times across stock pages',
        'Various stability fixes to reduce errors when loading filings and data'
      ]
    },
    {
      changeNumber: 2,
      date: 'Dec 8, 2025',
      type: 'Changed',
      title: 'Improved profile menu and login experience',
      details: [
        'Avatar now uses a single-color initial, and menu items show pointer hover states for clarity',
        'Auth0 session now persists across reloads with refresh tokens to speed up profile loading'
      ]
    },
    {
      changeNumber: 1,
      date: 'Dec 7, 2025',
      type: 'Fixed',
      title: 'Fixed a bug with the tutorial page',
      details: [
        'The tutorial page was not loading properly due to an issue with the navigation drawers. This has been resolved and the tutorial page should now work correctly.'
      ]
    }
];

const getTypeColor = (type) => {
  switch (type) {
    case 'Fixed':
      return 'orange.500';
    case 'Added':
      return 'green.500';
    case 'Changed':
      return 'blue.500';
    case 'Improved':
      return 'teal.500';
    case 'Removed':
      return 'red.500';
    default:
      return 'gray.500';
  }
};

const Changelog = () => (
  <Box p={8} maxW="1000px" mx="auto">
    <VStack spacing={8} align="stretch">
      <Heading as="h1" size="xl">
        Changelog
      </Heading>

      {changelogEntries.map((entry) => (
        <Box key={entry.changeNumber} p={6} borderWidth="1px" borderRadius="lg" boxShadow="sm">
          <VStack spacing={4} align="stretch">
            <Flex justifyContent="space-between" alignItems="center">
              <Box>
                <Text
                  as="span"
                  fontSize="sm"
                  fontWeight="bold"
                  color={getTypeColor(entry.type)}
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  {entry.type}
                </Text>
                <Text as="span" fontSize="sm" color="fg.subtle" ml={2}>
                  - {entry.date}
                </Text>
              </Box>
              <Text fontSize="sm" color="fg.muted">
                #{entry.changeNumber}
              </Text>
            </Flex>
            <Heading as="h3" size="md">
              {entry.title}
            </Heading>
            {entry.details && entry.details.length > 0 && (
              <Box pl={4}>
                <VStack spacing={2} align="stretch">
                  {entry.details.map((detail) => (
                    <Text key={`${entry.changeNumber}-${detail}`} fontSize="sm" color="fg.subtle" as="li">
                      {detail}
                    </Text>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>

        </Box>
      ))}
    </VStack>
  </Box>
);

export default Changelog;
